/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import { useEffect, useState } from "react";
import {
  prepareGeneralAuthData,
  prepareRadiusData,
  prepareRadiusPayload,
  prepareSamlData,
  prepareSamlPayload,
  prepareTacacsData,
  prepareTacacsPayload,
  validateRadius,
  validateSaml,
} from "./helpers";
import { useGetExternalAuthenticationQuery, useSetExternalAuthenticationMutation } from "../../../../store/api/deviceConfig/deviceConfigApiSlice";
import { OPERATIONTYPES, xsdPathStr } from "../../../../globals/constants";
import { setOpenToast } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { IRadius, IKeyValue, ITacacs, ISaml } from "../../types";
import { isError } from "../../../../utils/validations";
import { IGeneralAuth } from "../../../../store/api/deviceConfig/types";
import { errorMessage } from "../../../../utils/helpers";
import { useSelector } from "react-redux";
import { setErrorMsg } from "../../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";

export const useGeneralData = () => {
  const dispatch = useStoreDispatch();
  const { generalAuthentication } = xsdPathStr;
  const { data, refetch } = useGetExternalAuthenticationQuery({ actionType: OPERATIONTYPES.GETGENERALAUTH });
  const [generalAuth, setGeneralAuth] = useState<IGeneralAuth>({
    authenticationExternal: !!data ? data?.data[generalAuthentication.authenticationExternal] : "",
    authenticationSetup: !!data ? data?.data[generalAuthentication.authenticationSetup] : "",
  });

  const [setExternalAuthentication, statusGeneralAuth] = useSetExternalAuthenticationMutation();

  useEffect(() => {
    if (!!data && data?.data) {
      setGeneralAuth({
        authenticationExternal: !!data ? data?.data[generalAuthentication.authenticationExternal] : "",
        authenticationSetup: !!data ? data?.data[generalAuthentication.authenticationSetup] : "",
      });
    }
  }, [data, generalAuthentication.authenticationExternal, generalAuthentication.authenticationSetup]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const cancelHandler = async () => {
    await refetch();
    const generalAuthData = prepareGeneralAuthData(data);
    setGeneralAuth(generalAuthData);
  };

  const submitHandler = async () => {
    try {
      const payload = {
        actionType: OPERATIONTYPES.SETGENERALAUTH,
        errDescription: "No description",
        domainKey: "N/A",
        data: {
          [generalAuthentication.authenticationExternal]: generalAuth?.authenticationExternal,
          [generalAuthentication.authenticationSetup]: generalAuth?.authenticationSetup,
        },
      };

      let result: any;
      result = await setExternalAuthentication(payload);
      if (result?.error || statusGeneralAuth?.isError) throw result?.error;
      if (result.data?.status === 200) {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `The General Authentication data has been submited`,
          })
        );
        refetch();
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return { generalAuth, setGeneralAuth, submitHandler, cancelHandler };
};

export const useRadiusData = () => {
  const dispatch = useStoreDispatch();
  const { data, isLoading, refetch } = useGetExternalAuthenticationQuery({ actionType: OPERATIONTYPES.GETRADIUS });
  const radiusData = prepareRadiusData(data);
  const [radiusForm, setRadiusForm] = useState<IRadius>(radiusData);
  const [errMsg, setErrMsg] = useState<IKeyValue>({});

  const [setExternalAuthentication, statusRadius] = useSetExternalAuthenticationMutation();
  
  const errorMsg = useSelector(errorMsgSelector);
  const serverAddress ="serverAddress";
  const sharedSecret ="sharedSecret";
  const confirmSharedSecret ="confirmSharedSecret";
  const port ="port";
  const profileAttributeType ="profileAttributeType";
  const clearInputList = {serverAddress:"", sharedSecret:"", confirmSharedSecret:"", port:"", profileAttributeType:""};

  useEffect(() => {
    if (!radiusData) {
      setRadiusForm(radiusData);
    }
  }, [radiusData]);

  useEffect(() => {
    setErrMsg({});
    dispatch(setErrorMsg({...errorMsg, ...clearInputList }));
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const cancelHandler = async () => {
    setErrMsg({});
    dispatch(setErrorMsg({...errorMsg, ...clearInputList }));
    await refetch();
    const radiusData = prepareRadiusData(data);
    setRadiusForm(radiusData);
    setErrMsg({});
  };

  const submitHandler = async (event: any) => {
    event.preventDefault();

    try {
      let result: any;
      const errorMessages = validateRadius(radiusForm);
      setErrMsg({ ...errMsg, ...errorMessages });
      if (!isError(errorMessages)) {
        const payload = prepareRadiusPayload(radiusForm);
        result = await setExternalAuthentication(payload);
        if (result?.error || statusRadius?.isError) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The Radius data has been submited`,
            })
          );
          refetch();
        }
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return { radiusForm, setRadiusForm, isLoading, errMsg, setErrMsg, submitHandler, cancelHandler, serverAddress, sharedSecret, confirmSharedSecret, port, profileAttributeType };
};

export const useTacacsData = () => {
  const dispatch = useStoreDispatch();

  const { data, isLoading, refetch } = useGetExternalAuthenticationQuery({ actionType: OPERATIONTYPES.GETTACACS });
  const tacacsData = prepareTacacsData(data);
  const [tacacsForm, setTacacsForm] = useState<ITacacs>(tacacsData);
  const [errMsg, setErrMsg] = useState<IKeyValue>({});

  const [setExternalAuthentication, statusTacacs] = useSetExternalAuthenticationMutation();
  
  const errorMsg = useSelector(errorMsgSelector);
  const serverAddress ="serverAddress";
  const sharedSecret ="sharedSecret";
  const confirmSharedSecret ="confirmSharedSecret";
  const port ="port";
  const profileAttributeName ="profileAttributeName";
  const clearInputList = {serverAddress:"",sharedSecret:"",confirmSharedSecret:"", port:"", profileAttributeName:""};

  useEffect(() => {
    if (!tacacsData) {
      setTacacsForm(tacacsData);
    }
  }, [tacacsData]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    setErrMsg({});
    dispatch(setErrorMsg({...errorMsg, ...clearInputList }));
  }, []);

  const cancelHandler = async () => {
    setErrMsg({});
    dispatch(setErrorMsg({...errorMsg, ...clearInputList }));
    await refetch();
    const tacacsData = prepareTacacsData(data);
    setTacacsForm(tacacsData);
  };

  const submitHandler = async (event: any) => {
    event.preventDefault();

    try {
      let result: any;
      const errorMessages = validateRadius(tacacsForm);
      setErrMsg({ ...errMsg, ...errorMessages });
      if (!isError(errorMessages)) {
        const payload = prepareTacacsPayload(tacacsForm);
        result = await setExternalAuthentication(payload);
        if (result?.error || statusTacacs?.isError) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The Tacacs data has been submited`,
            })
          );
          refetch();
        }
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return { tacacsForm, setTacacsForm, isLoading, errMsg, setErrMsg, submitHandler, cancelHandler, serverAddress, sharedSecret, confirmSharedSecret, port, profileAttributeName };
};

export const useSamlData = () => {
  const dispatch = useStoreDispatch();

  const { data, isLoading, refetch } = useGetExternalAuthenticationQuery({ actionType: OPERATIONTYPES.GETSAML });
  const samlData = prepareSamlData(data);
  const [samlForm, setSamlForm] = useState<ISaml>(samlData);
  const [errMsg, setErrMsg] = useState<IKeyValue>({});
  const [setExternalAuthentication, statusSaml] = useSetExternalAuthenticationMutation();
  const errorMsg = useSelector(errorMsgSelector)
  const profileAttributeName ="profileAttributeName";
  const usernameAttributeName ="usernameAttributeName";
  const clearInputList : { [key: string]: string } = {profileAttributeName:"", usernameAttributeName:""}

  useEffect(() => {
    if (!samlData) {
      setSamlForm(samlData);
    }
  }, [samlData]);

  useEffect(() => {
    refetch();
  }, [refetch]);

   useEffect(() => {
    setErrMsg({});
    const samlData = prepareSamlData(data);
    samlData.authorizedServersList?.forEach((item: string, i: number) => {
        const index = i + 1;
        clearInputList[`samlServer${index}`] = "";
    });
    dispatch(setErrorMsg({...errorMsg, ...clearInputList }));
  }, []);

  const cancelHandler = async () => {
    setErrMsg({});
    await refetch();
    const samlData = prepareSamlData(data);
    samlData.authorizedServersList?.forEach((item: string, i: number) => {
        const index = i + 1;
        clearInputList[`samlServer${index}`] = "";
    });
    dispatch(setErrorMsg({...errorMsg, ...clearInputList }));
    setSamlForm(samlData);
  };

  const submitHandler = async (event: any) => {
    event.preventDefault();

    try {
      let result: any;

      const errorMessages = validateSaml(samlForm);
      setErrMsg({ ...errMsg, ...errorMessages });
      if (!isError(errorMessages)) {
        samlForm.authorizedServersList = samlForm.authorizedServersList.filter(Boolean);
        const payload = prepareSamlPayload(samlForm);
        result = await setExternalAuthentication(payload);
        if (result?.error || statusSaml?.isError) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The SAML data has been submited`,
            })
          );
          refetch();
        }
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return { samlForm, isLoading, setSamlForm, errMsg, setErrMsg, cancelHandler, submitHandler, profileAttributeName, usernameAttributeName };
};
